import React, { useState, useEffect, useCallback } from 'react';
import { useTranscription } from './hooks/useTranscription';
import trainingSimulationService from './api';

// Components
import SimulationAvatar from './components/SimulationAvatar';
import SimulationTranscription from './components/SimulationTranscription';
import ScenarioSelector from './components/ScenarioSelector';
import FeedbackPanel from './components/FeedbackPanel';
import SimulationControls from './components/SimulationControls';

const TrainingSimulation = () => {
    // State for scenario management
    const [selectedScenarioId, setSelectedScenarioId] = useState(null);
    const [scenario, setScenario] = useState(null);
    const [isSimulationActive, setIsSimulationActive] = useState(false);
    const [currentStep, setCurrentStep] = useState(0);
    const [conversationHistory, setConversationHistory] = useState([]);

    // State for avatar
    const [avatarResponse, setAvatarResponse] = useState('');
    const [isAvatarPlaying, setIsAvatarPlaying] = useState(false);
    const [isProcessingAudio, setIsProcessingAudio] = useState(false);

    // State for feedback
    const [feedback, setFeedback] = useState(null);
    const [isFeedbackLoading, setIsFeedbackLoading] = useState(false);

    // Get transcription functionality
    const {
        isRecording,
        hasStartedRecording,
        transcription,
        persistentLines,
        uploadStatus,
        handleStartRecording,
        handleStopRecording,
        transcriptionEndRef
    } = useTranscription(handleTranscriptionComplete);

    // Load scenario details when selected
    useEffect(() => {
        if (selectedScenarioId) {
            const loadScenario = async () => {
                try {
                    const data = await trainingSimulationService.getScenarioById(selectedScenarioId);
                    setScenario(data);
                    // Reset simulation state when scenario changes
                    setConversationHistory([]);
                    setCurrentStep(0);
                    setFeedback(null);
                } catch (err) {
                    console.error('Error loading scenario:', err);
                }
            };

            loadScenario();
        }
    }, [selectedScenarioId]);

    // Handle transcription completion
    function handleTranscriptionComplete(text) {
        if (!text || !isSimulationActive) return;

        // Add counselor's response to conversation history
        const newHistory = [
            ...conversationHistory,
            { role: 'counselor', content: text }
        ];

        setConversationHistory(newHistory);

        // Generate member (avatar) response
        generateAvatarResponse(newHistory);
    }

    // Generate avatar response based on counselor input
    const generateAvatarResponse = useCallback(async (history) => {
        if (!selectedScenarioId || !scenario) return;

        try {
            const response = await trainingSimulationService.generateResponse(
                selectedScenarioId,
                history[history.length - 1]?.content || '',
                history
            );

            setAvatarResponse(response.content);

            // Add member response to conversation history
            setConversationHistory(prev => [
                ...prev,
                { role: 'member', content: response.content }
            ]);

            // Increment step counter
            setCurrentStep(prev => prev + 1);

            // Check if we should generate feedback (every 2 exchanges or at end)
            if (currentStep > 0 && (currentStep % 2 === 0 || response.isEndOfScenario)) {
                generateFeedback([...history, { role: 'member', content: response.content }]);
            }
        } catch (err) {
            console.error('Error generating avatar response:', err);
        }
    }, [selectedScenarioId, scenario, currentStep]);

    // Generate feedback on counselor performance
    const generateFeedback = useCallback(async (history) => {
        if (!selectedScenarioId || !scenario) return;

        setIsFeedbackLoading(true);

        try {
            const feedbackData = await trainingSimulationService.getFeedback(
                selectedScenarioId,
                history
            );

            setFeedback(feedbackData);
        } catch (err) {
            console.error('Error generating feedback:', err);
        } finally {
            setIsFeedbackLoading(false);
        }
    }, [selectedScenarioId, scenario]);

    // Start the simulation
    const handleStartSimulation = useCallback(() => {
        if (!scenario) return;

        setIsSimulationActive(true);
        setConversationHistory([
            { role: 'member', content: scenario.initialPrompt }
        ]);
        setAvatarResponse(scenario.initialPrompt);
    }, [scenario]);

    // Restart the simulation
    const handleRestartSimulation = useCallback(() => {
        if (!scenario) return;

        setConversationHistory([]);
        setCurrentStep(0);
        setFeedback(null);
        setIsSimulationActive(false);

        // Small delay before starting again
        setTimeout(() => {
            handleStartSimulation();
        }, 500);
    }, [scenario, handleStartSimulation]);

    // Save progress
    const handleSaveProgress = useCallback(() => {
        // Implementation would depend on your backend requirements
        console.log('Saving progress:', {
            scenarioId: selectedScenarioId,
            conversationHistory,
            feedback
        });

        // Show success message to user
        alert('Progress saved successfully!');
    }, [selectedScenarioId, conversationHistory, feedback]);

    return (
        <div className="container mx-auto p-4">
            <h1 className="text-2xl font-bold mb-6">Collections Training Simulator</h1>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Left column - Scenario selection */}
                <div className="lg:col-span-1">
                    <ScenarioSelector
                        onScenarioSelect={setSelectedScenarioId}
                        selectedScenarioId={selectedScenarioId}
                    />

                    {feedback && (
                        <div className="mt-6">
                            <FeedbackPanel
                                feedback={feedback}
                                isLoading={isFeedbackLoading}
                            />
                        </div>
                    )}
                </div>

                {/* Right column - Simulation area */}
                <div className="lg:col-span-2">
                    <SimulationControls
                        onRestart={handleRestartSimulation}
                        onSaveProgress={handleSaveProgress}
                        onNextScenario={() => {/* Implementation for next scenario */ }}
                        onPreviousScenario={() => {/* Implementation for previous scenario */ }}
                        isSimulationActive={isSimulationActive}
                        onStartSimulation={handleStartSimulation}
                        canGoNext={true} // Logic to determine if next scenario is available
                        canGoPrevious={false} // Logic to determine if previous scenario is available
                    />

                    <div className="mt-6">
                        <h2 className="text-xl font-semibold mb-4">
                            {scenario ? scenario.title : 'Select a scenario to begin'}
                        </h2>

                        {scenario && (
                            <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6">
                                <h3 className="font-medium text-primaryDark">Scenario Context:</h3>
                                <p className="mt-1 text-sm text-gray-700">{scenario.description}</p>
                            </div>
                        )}

                        {isSimulationActive && (
                            <>
                                <div className="mb-6">
                                    <h3 className="font-semibold mb-2">Member</h3>
                                    <SimulationAvatar
                                        responseText={avatarResponse}
                                        isPlaying={isAvatarPlaying}
                                        onPlayComplete={() => setIsAvatarPlaying(false)}
                                        onPlayStart={() => setIsAvatarPlaying(true)}
                                    />
                                </div>

                                <div className="mb-6">
                                    <SimulationTranscription
                                        isRecording={isRecording}
                                        hasStartedRecording={hasStartedRecording}
                                        transcription={transcription}
                                        persistentLines={persistentLines}
                                        uploadStatus={uploadStatus}
                                        onStartRecording={handleStartRecording}
                                        onStopRecording={handleStopRecording}
                                    />
                                </div>

                                <div className="bg-gray-50 p-4 rounded-lg">
                                    <h3 className="font-semibold mb-2">Conversation History</h3>
                                    <div className="max-h-60 overflow-y-auto">
                                        {conversationHistory.map((entry, index) => (
                                            <div
                                                key={index}
                                                className={`p-3 rounded-lg mb-2 ${entry.role === 'member'
                                                        ? 'bg-blue-100 text-primaryDark'
                                                        : 'bg-green-100 text-green-800'
                                                    }`}
                                            >
                                                <div className="font-medium mb-1 text-xs uppercase">
                                                    {entry.role === 'member' ? 'Member' : 'You'}
                                                </div>
                                                <div className="text-sm">{entry.content}</div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TrainingSimulation;